<template>
    <div class="addons-list">
        <template v-for="(addon, index) in addons" :key="index">
            <!-- Quantité > 1 : sélecteur de quantité -->
            <div v-if="addonMaxQuantity(addon) > 1" class="addon-item addon-item--quantity">
                <div :class="{ disabled: addon.stocks.inStock <= 0 }" class="field-ctn field-border-gradient addon-quantity-row">
                    <span class="addon-quantity-label">{{ getAddonName(addon) }}</span>
                    <select
                        class="addon-quantity-select"
                        :disabled="addon.stocks.inStock <= 0"
                        :value="getQuantity(addon)"
                        @change="onQuantityChange(addon, Number(($event.target as HTMLSelectElement).value))"
                    >
                        <option v-for="n in quantityOptions(addon)" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>
            <!-- Quantité == 1 (ou non défini) : comportement checkbox existant -->
            <CheckboxVue
                v-else
                @changed="addOnChanged"
                :name="name"
                :value="addon._id"
                :label="getAddonName(addon)"
                :disabled="addon.stocks.inStock <= 0"
                :description="addon.description"
            />
        </template>
    </div>
</template>
<script setup lang="ts">
import CheckboxVue from '@/components/atoms/forms/Checkbox.vue';
import { Addon } from '@/interfaces/Addon';
import { CartTicket } from '@/interfaces/CartItem';
import { AppPrice } from '@/tools/Price';
import { toRefs } from 'vue';
import { isNumber } from 'lodash';

    const emit = defineEmits(['changed'])

    interface Props {
        ticketTypeId: string,
        addons: Addon[],
        cartTicket: CartTicket,
        name: string
    }

    const props = withDefaults(defineProps<Props>(), {
        addons: null,
    })

    const { cartTicket } = toRefs(props)

    /**
     * Retourne la quantité maximale sélectionnable pour un addon,
     * en tenant compte du selectableQuantity et du stock disponible.
     */
    const addonMaxQuantity = (addon: Addon): number => {
        const configMax = addon.selectableQuantity ?? 1
        if (configMax <= 1) return 1
        const available = addon.stocks.inStock
        if (available <= 0) return 1
        return Math.min(configMax, available)
    }

    /**
     * Génère les options 0..max pour le select.
     */
    const quantityOptions = (addon: Addon): number[] => {
        const max = addonMaxQuantity(addon)
        return Array.from({ length: max + 1 }, (_, i) => i)
    }

    /**
     * Retourne la quantité actuellement sélectionnée pour un addon
     * (nombre d'occurrences dans cartTicket._app.addons).
     */
    const getQuantity = (addon: Addon): number => {
        if (!cartTicket.value._app.addons) return 0
        return cartTicket.value._app.addons.filter((a: Addon) => a._id === addon._id).length
    }

    /**
     * Met à jour le tableau cartTicket._app.addons quand la quantité change.
     * Remplace toutes les occurrences existantes de cet addon par N nouvelles copies.
     */
    const onQuantityChange = (addon: Addon, newQty: number) => {
        const arr = cartTicket.value._app.addons
        if (!arr) return

        // Supprimer toutes les occurrences existantes de cet addon (mutation in-place)
        for (let i = arr.length - 1; i >= 0; i--) {
            if (arr[i]._id === addon._id) {
                arr.splice(i, 1)
            }
        }

        // Ajouter N copies
        for (let i = 0; i < newQty; i++) {
            arr.push(addon)
        }
    }

    /**
     * Gestion checkbox (comportement inchangé pour selectableQuantity == 1).
     */
    const addOnChanged = (val: {value: string, checked: boolean}) => {

        if (cartTicket.value._app.addons) {
            const selectedAddon = props.addons.find((addon: Addon) => addon._id == val.value)
            if (val.checked && selectedAddon) {
                cartTicket.value._app.addons?.push(selectedAddon);
            }
            if (!val.checked && selectedAddon) {
                const currentIndex = cartTicket.value._app.addons?.findIndex((addon: Addon) => (addon && addon._id === val.value))
                if (isNumber(currentIndex) && currentIndex >= 0) {
                    cartTicket.value._app.addons?.splice(currentIndex, 1)
                }
            }
        }

    }

    const getAddonName = (addon: Addon) => {
        const price = new AppPrice(addon.price).divide100().toString()
        return `${addon.name} (${price})`
    }

</script>
